import React, { useEffect, useRef, useState } from "react";
import { FaSpinner, FaPlay, FaPause, FaCheck, FaChevronDown, FaChevronRight } from "react-icons/fa";

interface Batch { id: number; name: string; isActive?: boolean; }

interface Submission {
  id: number;
  studentId: number;
  studentName: string;
  sequenceNumber: number;
  topic: string | null;
  audioUrl: string | null;
  originalFileName: string | null;
  status: "DRAFT" | "SUBMITTED" | "REVIEWED";
  uploadedAt: string | null;
  submittedAt: string | null;
  feedback: string | null;
  feedbackBy: string | null;
  feedbackAt: string | null;
}

interface Props {
  apiBase: string;        // e.g. /api/admin or /api/mentor
  batchesUrl: string;     // e.g. /api/admin/batches or /api/mentor/my-batches
}

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    DRAFT: "bg-gray-100 text-gray-500",
    SUBMITTED: "bg-blue-100 text-blue-700",
    REVIEWED: "bg-green-100 text-green-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? ""}`}>{status}</span>;
};

const AudioReviewPage: React.FC<Props> = ({ apiBase, batchesUrl }) => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState<number | null>(null);
  const [feedbackMap, setFeedbackMap] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const audioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  useEffect(() => {
    fetch(batchesUrl, { credentials: "include" })
      .then((r) => r.json())
      .then((data: Batch[]) => {
        const active = data.filter(b => b.isActive !== false);
        setBatches(active);
        if (active.length > 0) setSelectedBatchId(active[0].id);
      })
      .catch(console.error)
      .finally(() => setLoadingBatches(false));
  }, [batchesUrl]);

  useEffect(() => {
    if (!selectedBatchId) return;
    setLoadingSubmissions(true);
    setSubmissions([]);
    setExpandedStudent(null);
    fetch(`${apiBase}/audio-submissions/batch/${selectedBatchId}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: Submission[]) => {
        setSubmissions(data);
        const fb: Record<number, string> = {};
        data.forEach((s) => { fb[s.id] = s.feedback ?? ""; });
        setFeedbackMap(fb);
      })
      .catch(console.error)
      .finally(() => setLoadingSubmissions(false));
  }, [selectedBatchId, apiBase]);

  const saveFeedback = async (id: number) => {
    setSavingId(id);
    try {
      const r = await fetch(`${apiBase}/audio-submissions/${id}/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback: feedbackMap[id] ?? "" }),
      });
      const updated: Submission = await r.json();
      setSubmissions((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } finally {
      setSavingId(null);
    }
  };

  const togglePlay = (id: number, url: string) => {
    if (playingId === id) {
      audioRefs.current.get(id)?.pause();
      setPlayingId(null);
    } else {
      if (playingId !== null) audioRefs.current.get(playingId)?.pause();
      let audio = audioRefs.current.get(id);
      if (!audio) {
        audio = new Audio(url);
        audio.onended = () => setPlayingId(null);
        audioRefs.current.set(id, audio);
      }
      audio.play();
      setPlayingId(id);
    }
  };

  // Group submissions by student
  const byStudent = submissions.reduce<Record<number, { name: string; subs: Submission[] }>>((acc, s) => {
    if (!acc[s.studentId]) acc[s.studentId] = { name: s.studentName, subs: [] };
    acc[s.studentId].subs.push(s);
    return acc;
  }, {});

  if (loadingBatches) {
    return <div className="flex justify-center py-24"><FaSpinner className="animate-spin text-4xl text-[#009fe3]" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Batch selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Select Batch</label>
        <select
          value={selectedBatchId ?? ""}
          onChange={(e) => setSelectedBatchId(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent min-w-[220px]"
        >
          {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {loadingSubmissions ? (
        <div className="flex justify-center py-16"><FaSpinner className="animate-spin text-3xl text-[#009fe3]" /></div>
      ) : Object.keys(byStudent).length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
          No audio submissions for this batch.
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(byStudent).map(([studentIdStr, { name, subs }]) => {
            const studentId = Number(studentIdStr);
            const isOpen = expandedStudent === studentId;
            const pending = subs.filter((s) => s.status === "SUBMITTED").length;
            return (
              <div key={studentId} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Student header */}
                <button
                  onClick={() => setExpandedStudent(isOpen ? null : studentId)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <FaChevronDown size={12} className="text-gray-400" /> : <FaChevronRight size={12} className="text-gray-400" />}
                    <span className="font-semibold text-gray-800">{name}</span>
                    <span className="text-xs text-gray-400">{subs.length} submission{subs.length !== 1 ? "s" : ""}</span>
                  </div>
                  {pending > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      {pending} pending review
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Topic</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Submitted</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase w-full">Feedback</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Audio</th>
                          <th className="px-4 py-2.5"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...subs].reverse().map((sub) => (
                          <tr key={sub.id} className={sub.status === "SUBMITTED" ? "bg-blue-50/30" : ""}>
                            <td className="px-4 py-3 font-semibold text-gray-700">#{sub.sequenceNumber}</td>
                            <td className="px-4 py-3 text-xs text-[#009fe3] font-medium max-w-[180px] truncate">{sub.topic ?? "—"}</td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(sub.submittedAt)}</td>
                            <td className="px-4 py-3">{statusBadge(sub.status)}</td>
                            <td className="px-4 py-3">
                              {sub.status !== "DRAFT" ? (
                                <textarea
                                  value={feedbackMap[sub.id] ?? ""}
                                  onChange={(e) => setFeedbackMap((p) => ({ ...p, [sub.id]: e.target.value }))}
                                  placeholder="Write feedback..."
                                  rows={2}
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none min-w-[200px]"
                                />
                              ) : (
                                <span className="text-gray-300 text-xs italic">Not submitted yet</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {sub.audioUrl ? (
                                <button
                                  onClick={() => togglePlay(sub.id, sub.audioUrl!)}
                                  className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                >
                                  {playingId === sub.id ? <FaPause size={13} /> : <FaPlay size={13} />}
                                </button>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {sub.status !== "DRAFT" && (
                                <button
                                  onClick={() => saveFeedback(sub.id)}
                                  disabled={savingId === sub.id}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-[#009fe3] hover:bg-[#0088cc] text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 whitespace-nowrap"
                                >
                                  {savingId === sub.id ? <FaSpinner className="animate-spin" size={11} /> : <FaCheck size={11} />}
                                  Save
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AudioReviewPage;
